import { Decimal } from 'decimal.js';

/**
 * Money/decimal display helpers backed by decimal.js so we never do float math on
 * currency amounts that arrive as strings from the backend.
 */

/** Format a currency amount. Defaults to AUD (TipMi's home market). */
export function formatMoney(value: Decimal.Value | null | undefined, currency = 'AUD'): string {
  const amount = new Decimal(value ?? 0);
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
  }).format(amount.toNumber());
}

/** Full currency amount with an explicit ISO code (e.g. `A$1.00 AUD`). */
export function formatMoneyWithCode(
  value: Decimal.Value | null | undefined,
  currency = 'AUD',
): string {
  return `${formatMoney(value, currency)} ${currency}`;
}

/** Fixed-decimal string (no currency symbol). */
export function formatDecimal(value: Decimal.Value | null | undefined, decimalPlaces = 2): string {
  return new Decimal(value ?? 0).toFixed(decimalPlaces);
}

/** Compact number formatting (1.2K, 3.4M) for engagement counts etc. */
export function formatCompact(value: Decimal.Value | null | undefined): string {
  const n = new Decimal(value ?? 0).toNumber();
  return new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
}

/**
 * Compact currency with an explicit ISO code so reporting currency stays readable
 * (e.g. `A$1.2K AUD`). Pass `{ withCode: false }` for tight chart axis labels.
 */
export function formatCompactMoney(
  value: Decimal.Value | null | undefined,
  currency = 'AUD',
  options?: { withCode?: boolean },
): string {
  const n = new Decimal(value ?? 0).toNumber();
  const amount = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n);
  if (options?.withCode === false) return amount;
  return `${amount} ${currency}`;
}
